/**
 * /journals — journal list page.
 *
 * Fetches GET /api/journals with optional status and period_id filters.
 * Clicking a row navigates to /journals/:journalId.
 * "New Journal" button links to /journals/new (existing page).
 *
 * Note: the /api/journals endpoint returns up to `limit` rows and accepts
 * status and period_id query params. For a date-range or source filter we
 * derive from the available query params.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { periodsListQueryOptions } from "@/lib/queries";
import type { Journal } from "@/lib/queries";
import { JournalsGrid } from "@/grids/JournalsGrid";

export const Route = createFileRoute("/_auth/journals/")({
  component: JournalsListPage,
});

const JOURNAL_STATUSES = ["draft", "posted", "reversed"] as const;
const JOURNAL_SOURCES = [
  "manual", "ap_invoice", "ar_invoice", "payment",
  "depreciation", "fx_reval", "period_close",
  "wip_journal", "progress_billing", "job_cost", "money_fact",
  "bank_rec", "asset_acquisition",
] as const;

function JournalsListPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(200);

  const periodsQuery = useQuery(periodsListQueryOptions());

  // Build URL from filters
  const queryKey = useMemo(
    () => ["journals", "list", { statusFilter, sourceFilter, periodId, search, limit }] as const,
    [statusFilter, sourceFilter, periodId, search, limit],
  );

  const journalsQuery = useQuery({
    queryKey,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (statusFilter) sp.set("status", statusFilter);
      if (sourceFilter) sp.set("source", sourceFilter);
      if (periodId) sp.set("period_id", periodId);
      if (search.trim()) sp.set("search", search.trim());
      sp.set("limit", String(limit));
      return api<Journal[]>(`/api/journals?${sp.toString()}`);
    },
  });

  const journals = journalsQuery.data ?? [];

  const filtered = journals;

  function reset() {
    setStatusFilter(""); setSourceFilter(""); setPeriodId(""); setSearch("");
  }

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Journals</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {journalsQuery.isLoading ? "Loading…" : `${filtered.length} journal${filtered.length === 1 ? "" : "s"}`}
          </span>
          <div className="flex-1" />
          {journalsQuery.isFetching && !journalsQuery.isLoading && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Refreshing…</span>
          )}
          <Link
            to="/journals/new"
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New Journal
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex flex-wrap items-end gap-3 text-sm dark:bg-gray-900 dark:border-gray-800">
        <Field label="Status">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {JOURNAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Source">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {JOURNAL_SOURCES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Period">
          <select
            value={periodId}
            onChange={(e) => setPeriodId(e.target.value)}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            <option value="">all</option>
            {(periodsQuery.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                P{p.period_no} · {p.start_date} → {p.end_date} · {p.status}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Search (journal# / desc)">
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="journal no or description"
            className="border border-slate-300 rounded-md px-2 py-1 text-sm w-52 dark:border-gray-700"
          />
        </Field>
        <Field label="Max rows">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
          >
            {[100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <button
          onClick={reset}
          className="text-xs text-slate-500 hover:text-slate-900 underline ml-1 dark:text-slate-400"
        >
          reset
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {journalsQuery.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}
        {journalsQuery.error && (
          <div className="text-sm text-red-600">
            Error: {(journalsQuery.error as Error).message}
          </div>
        )}
        {!journalsQuery.isLoading && filtered.length === 0 && (
          <div className="text-sm text-slate-500 dark:text-slate-400">
            No journals match the current filters.
          </div>
        )}
        {!journalsQuery.isLoading && filtered.length > 0 && (
          <JournalsGrid journals={filtered} loading={journalsQuery.isLoading} />
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 dark:text-slate-400">
        {label}
      </span>
      {children}
    </div>
  );
}
